import { useState, useCallback } from 'react';
import { message } from 'antd';
import { supabase } from '../../../../lib/supabase';
import { PricingRule, RuleComponent } from '../types';

export const usePricingData = () => {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState<PricingRule[]>([]);

  // Load pricing rules with aggregated data
  const loadPricingRules = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data: rulesData, error } = await supabase
        .from("pricing_rules")
        .select(`
          id, name, transport_type, origin_port, destination_port, 
          shipment_type, incoterm, is_active
        `)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;

      // Fetch reference data for lookups
      const [
        { data: transportTypes },
        { data: originPorts },
        { data: destinationPorts },
        { data: bookingTypes },
        { data: incoterms },
      ] = await Promise.all([
        supabase.from("booking_transport_types").select("id, name"),
        supabase.from("booking_origin_port").select("id, name"),
        supabase.from("booking_destination_port").select("id, name"),
        supabase.from("bookings_type").select("id, name"),
        supabase.from("bookings_incoterm").select("id, name"),
      ]);

      // Get component counts and totals for each rule
      const rulesWithStats = await Promise.all(
        (rulesData || []).map(async (rule) => {
          const { data: components } = await supabase
            .from("pricing_values")
            .select("value, currency")
            .eq("pricing_rule_id", rule.id);

          const total = components?.reduce((sum, comp) => sum + Number(comp.value), 0) || 0;

          // Find related data names
          const transportType = transportTypes?.find(t => t.id === rule.transport_type);
          const originPort = originPorts?.find(p => p.id === rule.origin_port);
          const destinationPort = destinationPorts?.find(p => p.id === rule.destination_port);
          const bookingType = bookingTypes?.find(b => b.id === rule.shipment_type);
          const incoterm = incoterms?.find(i => i.id === rule.incoterm);

          return {
            ...rule,
            transport_type_name: transportType?.name || "Any Transport",
            origin_port_name: originPort?.name || "Any Origin",
            destination_port_name: destinationPort?.name || "Any Destination",
            shipment_type_name: bookingType?.name || "Any Booking Type",
            incoterm_name: incoterm?.name || "Any Incoterm",
            total_cost: total,
            component_count: components?.length || 0,
            last_updated: new Date().toISOString(),
          };
        })
      );

      setRules(rulesWithStats);
    } catch (error) {
      console.error("Error loading pricing rules:", error);
      message.error("Failed to load pricing data");
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load components for a specific rule
  const loadRuleComponents = useCallback(async (ruleId: number): Promise<RuleComponent[]> => {
    try {
      const { data, error } = await supabase
        .from("pricing_values")
        .select("id, pricing_rule_id, pricing_component_id, value, currency")
        .eq("pricing_rule_id", ruleId);

      if (error) throw error;

      // Get the component details separately
      const componentIds = (data || []).map(comp => comp.pricing_component_id);
      const { data: componentsData } = await supabase
        .from("pricing_components")
        .select("id, name, component_type, calculation_type")
        .in("id", componentIds);

      return (data || []).map(comp => {
        const component = componentsData?.find(c => c.id === comp.pricing_component_id);
        return {
          id: comp.id,
          pricing_rule_id: comp.pricing_rule_id,
          pricing_component_id: comp.pricing_component_id,
          component_name: component?.name || "Unknown Component",
          component_type: component?.component_type || "unknown",
          calculation_type: component?.calculation_type || "fixed",
          value: Number(comp.value),
          currency: comp.currency,
        };
      });
    } catch (error) {
      console.error("Error loading rule components:", error);
      message.error("Failed to load rule components");
      return [];
    }
  }, []);

  // Delete pricing rule
  const deleteRule = useCallback(async (ruleId: number) => {
    try {
      setLoading(true);

      // Delete components first
      await supabase
        .from("pricing_values")
        .delete()
        .eq("pricing_rule_id", ruleId);

      // Delete rule
      const { error } = await supabase
        .from("pricing_rules")
        .delete()
        .eq("id", ruleId);

      if (error) throw error;

      message.success("Pricing rule deleted successfully!");
      await loadPricingRules();
      
      return true;
    } catch (error) {
      console.error("Error deleting rule:", error);
      message.error("Failed to delete pricing rule");
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadPricingRules]);

  // Copy rule
  const copyRule = useCallback(async (rule: PricingRule) => {
    try {
      setLoading(true);

      // Create new rule
      const { data: newRule, error: ruleError } = await supabase
        .from("pricing_rules")
        .insert({
          name: `${rule.name} (Copy)`,
          transport_type: rule.transport_type,
          origin_port: rule.origin_port,
          destination_port: rule.destination_port,
          shipment_type: rule.shipment_type,
          incoterm: rule.incoterm,
          is_active: true,
          valid_from: new Date().toISOString(),
        })
        .select()
        .single();

      if (ruleError) throw ruleError;

      // Copy components
      const { data: components } = await supabase
        .from("pricing_values")
        .select("pricing_component_id, value, currency")
        .eq("pricing_rule_id", rule.id);

      if (components && components.length > 0) {
        const componentInserts = components.map(comp => ({
          pricing_rule_id: newRule.id,
          pricing_component_id: comp.pricing_component_id,
          value: comp.value,
          currency: comp.currency,
        }));

        const { error: componentsError } = await supabase
          .from("pricing_values")
          .insert(componentInserts);

        if (componentsError) throw componentsError;
      }

      message.success("Rule copied successfully!");
      await loadPricingRules();
      return true;
    } catch (error) {
      console.error("Error copying rule:", error);
      message.error("Failed to copy rule");
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadPricingRules]);

  return {
    loading,
    rules,
    loadPricingRules,
    loadRuleComponents,
    deleteRule,
    copyRule,
  };
}; 